import React from 'react'
import { FormattedMessage } from 'react-intl'
import capitalizeString from 'utils/capitalize'
import ArrowRight24 from 'assets/icons/arrow-right_24px'
import EditIcon from 'assets/icons/edit-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import ActionsMenu from 'features/ActionMenu'
import { ViewActionStyle } from './style'
import { formatDateYear } from 'utils/formatDateYear'
import {
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { INDIVIDUAL_ROLE_ID } from 'constants/roles'
import { canViewSiteInMySites } from 'utils/rolePermissions'
import { createNavigateReviewDetails } from 'utils/navigationHandlers'
import { editOnly } from 'routing/constants'

function isEditOnlyStatus (statusId) {
  return statusId === SITE_STATUS_EDIT_ONLY_ID || statusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
}

function isDraftOrEditOnlyStatus (statusId) {
  return statusId === SITE_STATUS_DRAFT_ID || statusId === SITE_STATUS_EDIT_ONLY_ID
}

/**
 * Edit/Remove in All assessments only when the current user owns the draft
 * (proposer or co-proposer), regardless of privileged role.
 */
export function canManageReassessmentDraftActions (site, profile) {
  if (!isDraftOrEditOnlyStatus(site?.SiteStatusId ?? site?.SiteStatus?.id)) {
    return false
  }
  if (profile?.id == null) {
    return false
  }
  return canViewSiteInMySites(site, { id: profile.id, RoleId: INDIVIDUAL_ROLE_ID })
}

function normalizeReassessmentSite (site, parentName) {
  const statusId = site.SiteStatusId
  const editOnlySiteStatus = isEditOnlyStatus(statusId)
  return {
    ...site,
    name: site.name || parentName,
    ProposerId: site.ProposerId || site.proposerId,
    SiteStatus: site.SiteStatus || { id: statusId },
    reassessmentFlag: !editOnlySiteStatus,
    historyId: editOnlySiteStatus ? (site.sitehistoryId || site.historyId) : site.historyId
  }
}

function buildViewAction () {
  return (
    <ViewActionStyle>
      <p>View</p>
      <ArrowRight24 />
    </ViewActionStyle>
  )
}

function buildDraftActions (normalizedSite, navigate, setSiteForDeleting, setEditOnlySiteToReject) {
  const editOnlySiteStatus = isEditOnlyStatus(normalizedSite.SiteStatusId)
  const editOnlyRoute = editOnlySiteStatus ? editOnly : null

  const dropdownItems = [
    {
      icon: <EditIcon />,
      title: 'edit',
      onClick: createNavigateReviewDetails(
        normalizedSite,
        navigate,
        editOnlyRoute,
        editOnlySiteStatus
      )
    },
    {
      icon: <RemoveIcon />,
      title: 'Remove',
      onClick: () => {
        if (editOnlySiteStatus) {
          setEditOnlySiteToReject?.({ ...normalizedSite, isApproved: false, delete: true })
          return
        }
        setSiteForDeleting?.(normalizedSite)
      }
    }
  ]

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <ActionsMenu dropdownItems={dropdownItems} />
    </div>
  )
}

export const makeSitesRows = (sites = [], options = {}) => {
  const {
    profile,
    navigate,
    setSiteForDeleting,
    setEditOnlySiteToReject,
    parentSiteName
  } = options

  return sites
    .toSorted((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((site) => {
      const formattedDate = formatDateYear(site.createdAt)
      const time = site.createdAt
        ? new Date(site.createdAt).toLocaleTimeString([], { hour12: true })
        : new Date().toLocaleTimeString([], { hour12: true })
      const editOnlySiteStatus = isEditOnlyStatus(site.SiteStatusId)
      const normalizedSite = normalizeReassessmentSite(site, parentSiteName)
      const showDraftActions = canManageReassessmentDraftActions(normalizedSite, profile)

      return {
        proposer: `${capitalizeString(site.User?.lastName)} ${capitalizeString(
          site.User?.firstName
        )}`,
        date: formattedDate,
        time,
        actions: showDraftActions
          ? buildDraftActions(
            normalizedSite,
            navigate,
            setSiteForDeleting,
            setEditOnlySiteToReject
          )
          : buildViewAction(),
        id: site.id,
        historyId: editOnlySiteStatus ? site.sitehistoryId : site.historyId,
        siteStatusId: site.SiteStatusId
      }
    })
}

export const makeSitesColumns = () => {
  return [
    {
      id: 'proposer',
      header: (
        <FormattedMessage id='proposer' />
      ),
      accessorKey: 'proposer',
      style: {
        width: '27%'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'date',
      header: (
        <FormattedMessage id='date' />
      ),
      accessorKey: 'date',
      style: {
        width: '23%'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'time',
      header: (
        <FormattedMessage id='time' />
      ),
      accessorKey: 'time',
      style: {
        width: '13%'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'actions',
      header: <FormattedMessage id='actions' />,
      accessorKey: 'actions',
      style: {
        width: '15%'
      },
      cell: ({ getValue }) => getValue()
    }
  ]
}
